// Rate-carry-over: when a user clicks a property from search results, we
// stash the exact rate they saw (offerId, mappedRoomId, price breakdown,
// cancellation, board) in localStorage. The property page reads it and
// injects it into its rates list — so the price they clicked is always
// visible, even if LiteAPI's rate check on the property page returns a
// different set (which happens: coord search vs hotelId search differ).

const KEY_PREFIX = "alohaOffer:";
// LiteAPI stamps each rates response with `et: 10800`, i.e. offers stay valid
// for 3 hours. The old 15-minute TTL threw away perfectly good carried offers
// and was the main reason a carry-over "randomly" stopped working after the
// user browsed for a while. Stay just inside the real window.
const TTL_MS = 165 * 60 * 1000;  // 2h45m
// One search stashes an entry per priced hotel (~90 hotels, ~3.5KB each,
// ~310KB a search). With a 3-hour TTL those pile up, and on a tighter quota
// (mobile Safari) setItem starts throwing — which stashOffer swallows, so
// carry-over would silently stop working. Sweep expired keys before writing.
function sweepExpired() {
  try {
    const now = Date.now();
    for (const k of Object.keys(localStorage)) {
      if (!k.startsWith(KEY_PREFIX)) continue;
      try {
        const p = JSON.parse(localStorage.getItem(k) || "null");
        if (!p || now - (p.savedAt || 0) > TTL_MS) localStorage.removeItem(k);
      } catch { localStorage.removeItem(k); }
    }
  } catch {}
}

function occupancyKey(adults, children) {
  const kids = (children || []).slice().sort((a, b) => a - b).join(",");
  return `${adults || 2}${kids ? "-" + kids : ""}`;
}

export function carryKey(hotelId, checkin, checkout, adults, children) {
  return `${KEY_PREFIX}${hotelId}:${checkin}:${checkout}:${occupancyKey(adults, children)}`;
}

// Save the cheapest rate we showed the user (plus the surrounding room type
// so we can reconstruct a full variant on the property page). We keep the
// original rate object so its exact shape is preserved for prebook.
export function stashOffer({ hotelId, checkin, checkout, adults, children, rate, roomType, stayNights }) {
  if (typeof localStorage === "undefined") return;
  try {
    const payload = {
      hotelId, checkin, checkout,
      adults: adults || 2,
      children: children || [],
      stayNights: stayNights || 1,
      rate,           // full rate object as returned by LiteAPI
      roomType: {     // only the fields we need — keeps the blob small
        offerId: roomType?.offerId,
        offerRetailRate: roomType?.offerRetailRate,
        suggestedSellingPrice: roomType?.suggestedSellingPrice,
        priceType: roomType?.priceType,
        name: roomType?.name
      },
      savedAt: Date.now()
    };
    const key = carryKey(hotelId, checkin, checkout, adults, children);
    try {
      localStorage.setItem(key, JSON.stringify(payload));
    } catch (quotaErr) {
      // Out of room. Drop expired entries and retry once — losing the carry
      // silently is exactly the failure we're trying to eliminate.
      sweepExpired();
      localStorage.setItem(key, JSON.stringify(payload));
    }
    console.log("[rateCarry] STASH", key, {
      offerId: payload.roomType.offerId,
      mappedRoomId: rate?.mappedRoomId,
      rateId: rate?.rateId,
      boardType: rate?.boardType,
      retailTotal: rate?.retailRate?.total?.[0]?.amount
    });
  } catch (err) { console.warn("[rateCarry] stash failed:", err); }
}

export function readOffer(hotelId, checkin, checkout, adults, children) {
  if (typeof localStorage === "undefined") return null;
  const key = carryKey(hotelId, checkin, checkout, adults, children);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      console.log("[rateCarry] READ miss — no entry for", key);
      // Show what IS in storage so we can spot key mismatches
      const alohaKeys = Object.keys(localStorage).filter(k => k.startsWith("alohaOffer:"));
      if (alohaKeys.length) console.log("[rateCarry]   available keys:", alohaKeys);
      return null;
    }
    const payload = JSON.parse(raw);
    if (Date.now() - (payload.savedAt || 0) > TTL_MS) {
      localStorage.removeItem(key);
      console.log("[rateCarry] READ expired for", key);
      return null;
    }
    console.log("[rateCarry] READ hit", key, {
      offerId: payload.roomType?.offerId,
      mappedRoomId: payload.rate?.mappedRoomId,
      rateId: payload.rate?.rateId,
      retailTotal: payload.rate?.retailRate?.total?.[0]?.amount
    });
    return payload;
  } catch (err) { console.warn("[rateCarry] read failed:", err); return null; }
}

// Return a rate object shaped like the ones LiteAPI returns from
// /rates for a hotelId call, so it can be merged into the property page's
// rate-processing pipeline.
export function offerAsRoomType(offer) {
  if (!offer) return null;
  return {
    offerId: offer.roomType?.offerId,
    offerRetailRate: offer.roomType?.offerRetailRate,
    suggestedSellingPrice: offer.roomType?.suggestedSellingPrice,
    priceType: offer.roomType?.priceType,
    name: offer.roomType?.name,
    rates: [offer.rate]
  };
}
